import bind from 'bind-decorator';

import { AvoCable } from '@/lib/avo-cable';
import { avo } from './avo-client';

// A client this far behind is better off refetching than paging through a backlog
const MAX_CATCH_UP_PAGES = 20;

// The sweep re-sends late-committing ids below the cursor, so remember enough to dedupe them
const APPLIED_MEMORY = 1_000;

interface CatchUpResponse {
  events: any[];
  cursor: string;
  more: boolean;
}

export enum LiveUpdatesChannel {
  V3_USERS = 'V3::UsersChannel',
  V3_UNADDRESSED = 'V3::UnaddressedChannel',
  V3_BROADCAST_CHANNEL = 'V3::BroadcastChannel',
  V3_BULK_ACTIONS = 'V3::BulkActionChannel',
  V3_TAGS = 'V3::TagsChannel',
  V3_TCR_CHANNEL = 'V3::TcrChannel',
  V3_USERS_NOTIFICATION_CHANNEL = 'V3::UsersNotificationChannel',
  V3_ORGANIZATIONS_CHANNEL = 'V3::OrganizationsChannel',
  V3_DATA_SOURCE_CHANNEL = 'V3::DataSourceChannel',
  V3_GROUP_CONVERSATIONS = 'V3::GroupConversationsChannel',
  V3_CALLS_CHANNEL = 'V3::CallsChannel',
}

interface ChannelSubscription {
  unsubscribe: () => void;
}

export interface LiveUpdatesConfig {
  channel: LiveUpdatesChannel;
  token: string;
  accountId?: string;
  onBatchReceived: (events: Array<any>, meta?: { recovered?: boolean }) => void;
  onConnect?: () => void;
  /** Fired on every connect after the first; ActionCable replays nothing broadcast while the socket was down */
  onReconnect?: () => void;
  onDisconnect?: (data?: { willAttemptReconnect?: boolean }) => void;
  /** Fired when the channel refuses the subscription (e.g. stale remember_token); nothing will ever arrive */
  onRejected?: () => void;
  subdomain?: string;
  flushInterval?: number;
  [key: string]: any;
}

export class LiveUpdates {
  protected subscription: ChannelSubscription;

  protected buffer = [];

  protected interval: NodeJS.Timeout | number | undefined;

  protected unsubscribed = false;

  protected flushInterval: number;

  protected hasConnected = false;

  // The last event applied, as the opaque key the endpoint understands; what the catch-up asks from
  protected cursor = '';

  // Set the moment events can have been missed, so live ones stop moving the cursor past the gap
  protected gapPending = false;

  protected gapGeneration = 0;

  // The sent_at the cursor sits at, so a republished older event cannot drag it backwards
  protected cursorAt = 0;

  protected applied = new Set<string>();

  protected appliedOrder: string[] = [];

  protected consumers = [] as Array<(events: Array<any>) => void>;

  accountId: LiveUpdatesConfig['accountId'];

  // The catch-up is scoped to one account so the query can use the (account_id, id) index
  protected subdomain: LiveUpdatesConfig['subdomain'];

  onBatchReceived: LiveUpdatesConfig['onBatchReceived'];

  protected onConnect: LiveUpdatesConfig['onConnect'];

  protected onReconnect: LiveUpdatesConfig['onReconnect'];

  protected onDisconnect: LiveUpdatesConfig['onDisconnect'];

  protected onRejected: LiveUpdatesConfig['onRejected'];

  constructor({
    channel, token, accountId, subdomain, onBatchReceived, flushInterval = 500,
    onConnect, onReconnect, onDisconnect, onRejected, ...rest
  }: LiveUpdatesConfig) {
    this.accountId = accountId;
    this.subdomain = subdomain;
    this.onBatchReceived = onBatchReceived;
    this.onConnect = onConnect;
    this.onReconnect = onReconnect;
    this.onDisconnect = onDisconnect;
    this.onRejected = onRejected;
    this.flushInterval = flushInterval;
    this.subscription = AvoCable.subscriptions.create(
      {
        channel,
        remember_token: token,
        ...(subdomain && { account_subdomain: subdomain }),
        ...rest,
      },
      {
        connected: this.connected,
        disconnected: this.disconnected,
        rejected: this.rejected,
        received: this.receive,
      },
    );
    this.startFlushing();
  }

  @bind
  connected(arg?: { reconnected?: boolean }) {
    // ActionCable reports this only when its monitor polled and saw the drop; frozen timers prevent that
    const reconnected = arg?.reconnected || this.hasConnected;
    this.hasConnected = true;
    if (reconnected) this.openGap(); // resubscribing closes one gap and opens another
    this.onConnect?.();
    if (reconnected) this.onReconnect?.();
  }

  @bind
  disconnected(data?: { willAttemptReconnect?: boolean }) {
    this.openGap();
    this.onDisconnect?.(data);
  }

  @bind
  rejected() {
    this.openGap();
    this.onRejected?.();
  }

  // Each gap gets its own generation so a catch-up can only close the one it started on
  protected openGap() {
    this.gapPending = true;
    this.gapGeneration += 1;
  }

  @bind
  receive(event: any) {
    if (event.type === 'restart') {
      // Never mid-call: reloading would drop it
      const reloadWhenIdle = window.sharedApi?.Twilio?.reloadWhenIdle;
      if (reloadWhenIdle) reloadWhenIdle();
      else window.location.reload();
      return;
    }
    if (this.accountId && event.account_id !== this.accountId) return;
    this.trackCursor(event); // after the filter: the catch-up is scoped to this account too
    // Recorded but never filtered: the server republishes the same id on status and media changes
    this.remember(event.cursor);
    this.buffer.push(event);
    if (this.flushInterval === 0) this.flush();
  }

  protected trackCursor(event: any) {
    // While a gap is open the catch-up must start from before it, not from whatever arrived after
    if (this.gapPending || !event.cursor) return;
    // sent_at orders the opaque key; a republish carries the original time and must not pull it back
    const at = Number(event.element?.sent_at);
    if (!Number.isFinite(at) || at < this.cursorAt) return;

    this.cursor = event.cursor;
    this.cursorAt = at;
  }

  protected forThisAccount(events: any[]) {
    if (!this.accountId) return events;
    return events.filter((event) => event.account_id === this.accountId);
  }

  // Never leave the cursor pinned at a point we can no longer trust; the caller refetches instead
  protected abandonCatchUp() {
    this.gapPending = false;
    this.cursor = '';
    this.cursorAt = 0;
  }

  /** Pulls everything after the last applied event; false means the caller must refetch instead */
  async fetchMissedEvents(): Promise<boolean> {
    if (!this.cursor) { // nothing applied yet, so there is no window to ask for
      this.abandonCatchUp();
      return false;
    }

    let after = this.cursor; // the endpoint decides how far below this a late commit can still count
    const generation = this.gapGeneration; // a gap opened after this point is not ours to close

    try {
      for (let page = 0; page < MAX_CATCH_UP_PAGES; page += 1) {
        const { events, cursor, more } = await avo.ajax<never, CatchUpResponse>({
          route: '/events.json',
          params: {
            after,
            subdomain: this.subdomain,
            // Late commits sort below the cursor, so ask for them once instead of on every page
            ...(page === 0 && { sweep: '1' }),
          },
        });

        // The sweep deliberately re-sends ids around the cursor, so drop the ones already applied
        const fresh = this.forThisAccount(events).filter((event) => this.markApplied(event.cursor));
        if (fresh.length > 0) this.pushBatch(fresh, { recovered: true }); // ascending, as the reducers prepend
        this.cursor = cursor; // the endpoint never hands back a point behind what was asked for
        this.cursorAt = Number(events[events.length - 1]?.element?.sent_at) || this.cursorAt;
        after = cursor;
        if (!more) {
          if (generation === this.gapGeneration) this.gapPending = false;
          return true;
        }
      }
      console.warn('users channel catch-up did not finish within the page limit');
    } catch (err) {
      console.warn('users channel catch-up failed', (err as any)?.status ?? err);
    }

    this.abandonCatchUp();
    return false;
  }

  @bind
  flush() {
    if (this.buffer.length > 0) {
      const { buffer } = this;
      this.buffer = [];
      this.pushBatch(buffer);
    }
  }

  @bind
  unsubscribe() {
    this.subscription.unsubscribe();
    this.unsubscribed = true;
    this.stopFlushing();
  }

  protected startFlushing() {
    this.stopFlushing();
    // flushInterval 0 delivers each event synchronously from receive() instead of
    // batching on a timer. Subscriptions that outlive a Turbolinks navigation need
    // this: turbofog clears every pending timer on turbolinks:request-start, which
    // would strand buffered events forever.
    if (this.flushInterval > 0) this.interval = setInterval(this.flush, this.flushInterval);
  }

  protected stopFlushing() {
    if (this.interval !== undefined) clearInterval(this.interval as NodeJS.Timeout);
    this.interval = undefined;
  }

  @bind
  addConsumer(consumer: (events: Array<any>) => void) {
    this.consumers.push(consumer);
  }

  @bind
  removeConsumer(consumer: (events: Array<any>) => void) {
    const index = this.consumers.indexOf(consumer);
    if (index > -1) {
      this.consumers.splice(index, 1);
    }
  }

  // Records what has been delivered so the sweep does not re-deliver it; sized well above a page
  protected remember(cursor: string) {
    if (!cursor || this.applied.has(cursor)) return;

    this.applied.add(cursor);
    this.appliedOrder.push(cursor);
    if (this.appliedOrder.length > APPLIED_MEMORY) this.applied.delete(this.appliedOrder.shift()!);
  }

  // True the first time a cursor is seen, so the sweep can drop what the live stream already brought
  protected markApplied(cursor: string) {
    if (!cursor) return true; // nothing to dedupe on, so let it through
    if (this.applied.has(cursor)) return false;

    this.remember(cursor);
    return true;
  }

  private pushBatch(events: Array<any>, meta?: { recovered?: boolean }) {
    this.onBatchReceived(events, meta);
    this.consumers.forEach((cb) => {
      try {
        cb(events);
      } catch (err) {
        console.error(err);
      }
    });
  }
}
